import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import InfiniteScroll from "react-infinite-scroll-component";
import get from "lodash/get";
import { Typography, useMediaQuery, Theme } from "@mui/material";
import { Investment } from "@bn/api/apiv1";
import { investmentToSecurityImagePayload, toCurrency } from "@helpers";
import {
  Modal,
  AutoTable,
  LoadingPlaceHolder,
  WithLoadingSpinner,
  InvestmentTableAddQuoteModal,
} from "@components";
import { useLoadMore, useSecurityImages, useTableSortWithData } from "@hooks";
import { EditInvestmentModal } from "../../edit/EditInvestmentModal";
import { useInvestmentPrivateNotesMap } from "../../hooks/useInvestmentPrivateNotesMap";
import {
  useTableConfig,
  InvestmentTableAdditionalProps,
} from "./useTableConfig";
import { MobileInvestmentItem } from "./MobileInvestmentItem";
import { InvestmentDetailModal } from "./InvestmentDetailModal";
import { DeleteInvestmentDialog } from "./DeleteInvestmentDialog";

const LOAD_MORE_STEP = 50;

export interface Props {
  investments: Investment[];
  className?: string;
  isLoading?: boolean;
  isFetching?: boolean;
  isPending?: boolean;
  formatCurrency?: (value: number, currency?: string) => string;
  /** Set when the table is shown inside a single depot, so the investment page
   * can be reached under that depot and show it in the breadcrumbs. */
  depotId?: number;
}

const getSortValue = (row: Investment, field: string) =>
  field === "name" ? row.standardisedName || row.name : get(row, field);

export const InvestmentsTable = ({
  className,
  investments,
  isLoading,
  isFetching,
  isPending,
  formatCurrency = toCurrency,
  depotId,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  const {
    data: sortedInvestments,
    sort,
    sortField,
    sortDirection,
  } = useTableSortWithData({
    data: investments,
    defaultSort: { field: "name", direction: "ASC" },
    getSortValue,
    saveKey: "investmentsTable",
  });

  const {
    visibleData: visibleInvestments,
    hasMore,
    loadMore,
  } = useLoadMore(sortedInvestments, LOAD_MORE_STEP);

  const [selectedInvestment, setSelectedInvestment] = useState<Investment>();
  const [editingInvestment, setEditingInvestment] = useState<Investment>();
  const [deletingInvestment, setDeletingInvestment] = useState<Investment>();

  const handleRowClick = useCallback((investment: Investment) => {
    (document.activeElement as HTMLElement | null)?.blur();
    setSelectedInvestment(investment);
  }, []);

  const handleCloseDetailModal = () => {
    setSelectedInvestment(undefined);
  };

  const handleAddBookingInvestment = useCallback(
    (investment: Investment) => {
      navigate(`/add-transaction-wizard/${investment.id}`);
    },
    [navigate]
  );

  const handleEditInvestment = useCallback((investment: Investment) => {
    setEditingInvestment(investment);
  }, []);

  const handleDeleteInvestment = useCallback((investment: Investment) => {
    setDeletingInvestment(investment);
  }, []);

  const config = useTableConfig(handleRowClick, {
    sortField,
    sortDirection,
    onSort: sort,
  });

  const infiniteScroll = {
    hasMore,
    next: loadMore,
    loader: null,
  };

  const [currentAddQuoteInvestment, setCurrentAddQuoteInvestment] =
    useState<Investment>();
  const [isAddQuoteModalShown, setIsAddQuoteModalShown] = useState(false);

  const handleInvestmentSelected = (investment: Investment) => {
    setCurrentAddQuoteInvestment(investment);
    setIsAddQuoteModalShown(true);
  };

  const handleCloseModal = () => {
    setCurrentAddQuoteInvestment(undefined);
    setIsAddQuoteModalShown(false);
  };

  const securityForLogos = useMemo(
    () => investments.map(investmentToSecurityImagePayload),
    [investments]
  );

  const images = useSecurityImages(securityForLogos);

  const investmentIds = useMemo(
    () => visibleInvestments.map((i) => i.id),
    [visibleInvestments]
  );
  const notesByInvestmentId = useInvestmentPrivateNotesMap(investmentIds);

  const additionalProps = useMemo<InvestmentTableAdditionalProps>(
    () => ({
      formatCurrency,
      images,
      notesByInvestmentId,
      onClickAddQuoteModal: handleInvestmentSelected,
      onAddBookingInvestment: handleAddBookingInvestment,
      depotId,
      onEditInvestment: handleEditInvestment,
      onDeleteInvestment: handleDeleteInvestment,
    }),
    [
      formatCurrency,
      images,
      notesByInvestmentId,
      handleAddBookingInvestment,
      handleEditInvestment,
      handleDeleteInvestment,
      depotId,
    ]
  );

  return (
    <>
      {isSm ? (
        isLoading ? (
          <LoadingPlaceHolder className={className} />
        ) : (
          <>
            {investments.length > 0 && (
              <Typography variant="body3" color="textSecondary" mb={1}>
                {t("domains.investments.investmentTable.entries", {
                  count: investments.length,
                })}
              </Typography>
            )}
            <WithLoadingSpinner isLoading={isFetching || false} minHeight={100}>
              <InfiniteScroll
                dataLength={visibleInvestments.length}
                next={loadMore}
                hasMore={hasMore}
                loader={null}
              >
                {visibleInvestments.map((investment) => (
                  <MobileInvestmentItem
                    key={investment.id}
                    investment={investment}
                    image={
                      images[investment.isin || ""] ||
                      images[investment?.tickerSymbol || ""]
                    }
                    noteComment={notesByInvestmentId[investment.id]}
                    depotId={depotId}
                    onRowClick={handleRowClick}
                    onClickAddQuoteModal={handleInvestmentSelected}
                    onAddBookingInvestment={handleAddBookingInvestment}
                    onEditInvestment={handleEditInvestment}
                    onDeleteInvestment={handleDeleteInvestment}
                  />
                ))}
              </InfiniteScroll>
            </WithLoadingSpinner>
          </>
        )
      ) : (
        <AutoTable
          settings={config}
          data={visibleInvestments}
          isPending={isPending}
          isFetching={isFetching}
          infiniteScroll={infiniteScroll}
          className={className}
          additionalProps={additionalProps}
          sx={{
            px: 2,
            mx: -2,
          }}
        />
      )}

      <Modal
        isOpen={isAddQuoteModalShown}
        onClose={handleCloseModal}
        title={t("domains.investments.investmentTable.addQuote.modalTitle")}
        width={600}
      >
        <InvestmentTableAddQuoteModal
          investment={currentAddQuoteInvestment}
          onClose={handleCloseModal}
        />
      </Modal>

      <InvestmentDetailModal
        investment={selectedInvestment}
        depotId={depotId}
        isOpen={!!selectedInvestment}
        onClose={handleCloseDetailModal}
        image={
          selectedInvestment
            ? images[selectedInvestment.isin || ""] ||
              images[selectedInvestment.tickerSymbol || ""]
            : undefined
        }
      />

      <EditInvestmentModal
        investment={editingInvestment}
        image={
          editingInvestment
            ? images[editingInvestment.isin || ""] ||
              images[editingInvestment.tickerSymbol || ""]
            : undefined
        }
        isOpen={!!editingInvestment}
        onClose={() => setEditingInvestment(undefined)}
      />

      {deletingInvestment && (
        <DeleteInvestmentDialog
          investmentId={deletingInvestment.id}
          isOpen
          onClose={() => setDeletingInvestment(undefined)}
        />
      )}
    </>
  );
};
